import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import LeadCaptureForm from "@/components/LeadCaptureForm";
import {
  ArrowRight,
  CheckCircle,
  FileText,
  Home,
  Key,
  Search,
  Shield,
} from "lucide-react";

const buyingSteps = [
  {
    number: "01",
    title: "Get Pre-Approved",
    description:
      "Before we even start looking, you need to know exactly what you can afford. I'll connect you with trusted lenders who won't waste your time.",
    icon: FileText,
  },
  {
    number: "02",
    title: "Define Your Goals",
    description:
      "What do you actually need vs. what you want? I'll help you prioritize so we don't waste time on homes that don't fit.",
    icon: Search,
  },
  {
    number: "03",
    title: "Find The Right Home",
    description:
      "I'll show you properties that actually match your criteria. No bait-and-switch tactics. Just real options.",
    icon: Home,
  },
  {
    number: "04",
    title: "Make Your Offer",
    description:
      "This is where I fight for you. I'll craft a competitive offer and negotiate to get you the best possible deal.",
    icon: Shield,
  },
  {
    number: "05",
    title: "Close & Get Keys",
    description:
      "I'll guide you through inspections, appraisals, and closing. Then you get your keys. Simple as that.",
    icon: Key,
  },
];

const expectations = [
  "Honest feedback on every property — even if it means telling you not to buy",
  "Daily updates on new listings that match your criteria",
  "Strategic negotiation to get you the best price and terms",
  "Protection from overpaying or getting stuck with problems",
  "Guidance through every step of the process",
  "Someone who actually answers their phone",
];

export default function BuyPage() {
  return (
    <div className="page-transition" data-testid="buy-page">
      {/* Hero */}
      <section className="py-24 md:py-32 bg-black">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl space-y-8">
            <div className="space-y-4">
              <div className="gold-line" />
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter uppercase text-white">
                Buy With <span className="text-[#D4AF37]">Me</span>
              </h1>
            </div>
            <p className="text-xl text-zinc-300 leading-relaxed">
              Buying a home is probably the biggest purchase you'll ever make.
              You need someone in your corner who knows the game and isn't
              afraid to tell you the truth.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact">
                <Button
                  className="bg-[#D4AF37] text-black hover:bg-[#F3E5AB] rounded-none font-bold uppercase tracking-widest px-8 py-6"
                  data-testid="buy-hero-contact-button"
                >
                  Start Your Search
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <a href="tel:+19092335411">
                <Button
                  className="bg-transparent border border-white text-white hover:bg-white hover:text-black rounded-none font-bold uppercase tracking-widest px-8 py-6"
                  data-testid="buy-hero-call-button"
                >
                  Call Me First
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold tracking-tight uppercase text-white">
              The <span className="text-[#D4AF37]">Buying</span> Process
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              No surprises. Here's exactly how we'll get you into your new home.
            </p>
          </div>

          <div className="relative">
            {/* Timeline line - hidden on mobile */}
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-[#D4AF37] to-[#D4AF37]/20" />

            <div className="space-y-12 md:space-y-24">
              {buyingSteps.map((step, index) => (
                <div
                  key={step.number}
                  className={`relative flex flex-col md:flex-row items-center gap-8 ${
                    index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                  }`}
                  data-testid={`buying-step-${step.number}`}
                >
                  {/* Content */}
                  <div
                    className={`flex-1 ${
                      index % 2 === 0 ? "md:text-right" : "md:text-left"
                    }`}
                  >
                    <div
                      className={`card-dark p-8 space-y-4 ${
                        index % 2 === 0 ? "md:ml-auto md:mr-12" : "md:mr-auto md:ml-12"
                      } max-w-lg`}
                    >
                      <span className="stroke-text text-6xl font-black">
                        {step.number}
                      </span>
                      <h3 className="text-2xl font-bold text-white">
                        {step.title}
                      </h3>
                      <p className="text-zinc-400 leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>

                  {/* Center icon */}
                  <div className="hidden md:flex w-16 h-16 bg-[#D4AF37] items-center justify-center z-10">
                    <step.icon className="w-8 h-8 text-black" />
                  </div>

                  {/* Spacer */}
                  <div className="flex-1 hidden md:block" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What To Expect */}
      <section className="py-24 md:py-32 bg-black">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <div className="gold-line" />
                <h2 className="text-4xl sm:text-5xl font-bold tracking-tight uppercase text-white">
                  What To <span className="text-[#D4AF37]">Expect</span>
                </h2>
              </div>
              <p className="text-zinc-400 leading-relaxed">
                Working with me means you get a real partner, not just someone
                who opens doors. Here's what I bring to the table:
              </p>

              <div className="space-y-4">
                {expectations.map((item) => (
                  <div
                    key={item}
                    className="flex items-start gap-3"
                    data-testid={`expectation-${index}`}
                  >
                    <CheckCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <span className="text-zinc-300">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="card-dark p-8 md:p-12 border-[#D4AF37]/30">
              <LeadCaptureForm
                type="buyer"
                title="Get Your Buyer Checklist"
                subtitle="A straightforward guide to everything you need before buying."
              />
            </div>
          </div>
        </div>
      </section>

      {/* Pre-Approval CTA */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center space-y-8">
          <h2 className="text-4xl sm:text-5xl font-bold tracking-tight uppercase text-white">
            First Step: <span className="text-[#D4AF37]">Get Pre-Approved</span>
          </h2>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            Don't waste time looking at homes you can't afford. Get pre-approved
            first, then we'll find your perfect home. I work with lenders who
            won't jerk you around.
          </p>
          <Link to="/contact">
            <Button
              className="bg-[#D4AF37] text-black hover:bg-[#F3E5AB] rounded-none font-bold uppercase tracking-widest px-8 py-6"
              data-testid="buy-preapproval-button"
            >
              Talk About Pre-Approval
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
